import { PERMISSIONS } from '@conversed/shared/src/central/constants/permissions';
import { PlatformFeatureType } from '@conversed/shared/src/central/types/platform-features.type';
import { StudioType } from '@conversed/shared/src/central/types/studio.type';
import { ThirdPartyIntegrationType } from '@conversed/shared/src/central/types/third-party-integrations.types';
import { lazyNamed } from '@helpers/lazyNamed';
import { lazy } from 'react';
import type { TSettingsSidebarElement } from '../../interfaces/ISettings';

const ContactLookupAdminPage = lazyNamed(
  () => import('@components/pages/ContactLookupAdminPage'),
  'ContactLookupAdminPage',
);
const HomeSettings = lazy(
  () => import('@components/settings-page/HomeSettings'),
);
const KnowledgeHubSettings = lazy(
  () => import('@components/settings-page/KnowledgeHubSettings'),
);
const CsatSettings = lazy(
  () => import('@components/settings-page/CsatSettings'),
);
const HotTopicsSettings = lazy(
  () => import('@components/settings-page/HotTopicsSettings'),
);
const EmailStudioSettings = lazyNamed(
  () => import('@components/settings-page/EmailStudioSettings'),
  'EmailStudioSettings',
);
const VoiceStudioSettings = lazyNamed(
  () => import('@components/settings-page/VoiceStudioSettings'),
  'VoiceStudioSettings',
);
const InviteEmailTemplateSettings = lazyNamed(
  () => import('@components/settings-page/InviteEmailTemplateSettings'),
  'InviteEmailTemplateSettings',
);
const PublicAPISettings = lazy(
  () => import('@components/settings-page/PublicAPISettings'),
);
const SubflowTemplatesSettings = lazy(
  () =>
    import(
      '@components/settings-page/subflow-templates/SubflowTemplatesSettings'
    ),
);
const TopDeskSettings = lazy(
  () => import('@components/settings-page/TopDeskSettings'),
);
const ZendeskSettings = lazy(
  () => import('@components/settings-page/ZendeskSettings'),
);
const RevisionsPage = lazyNamed(
  () => import('../pages/RevisionsPage'),
  'RevisionsPage',
);
const AgentSettings = lazyNamed(
  () => import('../settings-page/AgentSettings'),
  'AgentSettings',
);
const AppSettings = lazyNamed(
  () => import('../settings-page/AppSettings'),
  'AppSettings',
);
const CannedResponsesSettings = lazyNamed(
  () => import('../settings-page/CannedResponsesSettings'),
  'CannedResponsesSettings',
);
const ConversationKeywordsSettings = lazy(
  () => import('../settings-page/ConversationKeywordsSettings'),
);
const DebugSettings = lazyNamed(
  () => import('../settings-page/DebugSettings'),
  'DebugSettings',
);
const ExportSettings = lazyNamed(
  () => import('../settings-page/ExportSettings'),
  'ExportSettings',
);
const FacebookSettings = lazy(
  () => import('../settings-page/FacebookSettings'),
);
const GeneralSettings = lazy(() => import('../settings-page/GeneralSettings'));
const HandoverSettings = lazyNamed(
  () => import('../settings-page/HandoverSettings'),
  'HandoverSettings',
);
const InboxSettings = lazyNamed(
  () => import('../settings-page/InboxSettings'),
  'InboxSettings',
);
const LabelSettings = lazyNamed(
  () => import('../settings-page/LabelSettings'),
  'LabelSettings',
);
const LinkSettings = lazy(() => import('../settings-page/LinkSettings'));
const MemberSettings = lazy(() => import('../settings-page/MemberSettings'));
const RolesSettings = lazyNamed(
  () => import('../settings-page/RolesSettings'),
  'RolesSettings',
);
const TemplateResponsesSettings = lazy(
  () => import('../settings-page/TemplateResponsesSettings'),
);
const ValidationSettings = lazyNamed(
  () => import('../settings-page/ValidationSetttings'),
  'ValidationSettings',
);
const WebclientSettings = lazy(
  () => import('../settings-page/WebclientSettings'),
);

export const SettingsSidebarRoutes: TSettingsSidebarElement[] = [
  {
    icon: 'chat',
    title: 'settings.sidebar.conversation',
    path: 'conversations',
    elements: [
      {
        text: 'settings.sidebar.conversation.agents',
        path: 'agents',
        component: AgentSettings,
        permissions: [PERMISSIONS.AGENT.AGENT_LIST],
      },
      {
        text: 'settings.sidebar.conversation.inboxes',
        path: 'inboxes',
        component: InboxSettings,
        permissions: [PERMISSIONS.INBOX.INBOX_LIST],
      },
      {
        text: 'settings.sidebar.conversation.labels',
        path: 'labels',
        component: LabelSettings,
        permissions: [PERMISSIONS.LABEL.LABEL_LIST],
      },
      {
        text: 'settings.sidebar.conversation.canned-responses',
        path: 'canned-responses',
        component: CannedResponsesSettings,
        permissions: [PERMISSIONS.CANNED_RESPONSE.CANNED_RESPONSE_LIST],
      },
      {
        text: 'common.template-responses',
        path: 'template-responses',
        component: TemplateResponsesSettings,
        permissions: [PERMISSIONS.TEMPLATE_RESPONSE.TEMPLATE_RESPONSE_LIST],
      },
      {
        text: 'settings.sidebar.conversation.keywords',
        path: 'keywords',
        component: ConversationKeywordsSettings,
        permissions: [
          PERMISSIONS.CONVERSATION_KEYWORDS.CONVERSATION_KEYWORDS_LIST,
        ],
        platformFeature: PlatformFeatureType.ConversationKeywords,
      },
    ],
  },
  {
    icon: 'integration_instructions',
    title: 'settings.sidebar.integrations',
    path: 'integrations',
    elements: [
      {
        text: 'settings.sidebar.integrations.webclient',
        path: 'webclient',
        component: WebclientSettings,
        permissions: [PERMISSIONS.WEBCLIENT.WEBCLIENT_CONFIG_LIST],
      },
      {
        text: 'settings.sidebar.integrations.facebook',
        path: 'facebook',
        component: FacebookSettings,
        permissions: [PERMISSIONS.FACEBOOK.FACEBOOK_LIST],
      },
      {
        text: 'settings.sidebar.integrations.topdesk',
        path: 'topdesk',
        component: TopDeskSettings,
        permissions: [PERMISSIONS.COMPANY.COMPANY_GENERAL_SETTINGS_READ],
        thirdPartyIntegration: ThirdPartyIntegrationType.TopDesk,
      },
      {
        text: 'settings.sidebar.integrations.zendesk',
        path: 'zendesk',
        component: ZendeskSettings,
        permissions: [PERMISSIONS.COMPANY.COMPANY_GENERAL_SETTINGS_READ],
        thirdPartyIntegration: ThirdPartyIntegrationType.Zendesk,
      },
      {
        text: 'settings.sidebar.integrations.email-studio',
        path: 'email-studio',
        component: EmailStudioSettings,
        permissions: [PERMISSIONS.EMAIL_STUDIO.EMAIL_STUDIO_MANAGE],
        platformFeature: PlatformFeatureType.EmailAIStudio,
        studioTypes: [StudioType.EMAIL_AI],
      },
      {
        text: 'settings.sidebar.integrations.voice-studio',
        // * No platformFeature: Voice Studio is gated on studioTypes plus
        // * the permission below, under the existing superAdmin route gate.
        path: 'voice-studio',
        component: VoiceStudioSettings,
        permissions: [PERMISSIONS.VOICE_AI.VOICE_STUDIO_MANAGE],
        studioTypes: [StudioType.VOICE_AI],
      },
      {
        text: 'settings.sidebar.integrations.public-api-settings',
        path: 'public-api-settings',
        component: PublicAPISettings,
        permissions: [PERMISSIONS.PUBLIC_API_SETTINGS.PUBLIC_API_SETTINGS_EDIT],
      },
      {
        text: 'settings.sidebar.integrations.contact-lookup',
        path: 'contact-lookup-import',
        component: ContactLookupAdminPage,
        platformFeature: PlatformFeatureType.ContactLookup,
        permissions: [PERMISSIONS.COMPANY.COMPANY_GENERAL_SETTINGS_EDIT],
      },
      // {
      //   text: 'Viber',
      //   path: 'viber',
      // },
      // {
      //   text: 'KakaoTalk',
      //   path: 'kakao-talk',
      // },
      // {
      //   text: 'WhatsApp',
      //   path: 'whatsapp',
      // },
      // {
      //   text: 'Microsoft Teams',
      //   path: 'microsoft-teams',
      // },
    ],
  },
  {
    icon: 'settings',
    title: 'settings.sidebar.platform-settings',
    path: 'platform-settings',
    elements: [
      {
        text: 'settings.sidebar.platform-settings.general',
        path: 'general',
        component: GeneralSettings,
        permissions: [PERMISSIONS.COMPANY.COMPANY_GENERAL_SETTINGS_READ],
      },
      // * The four platform settings pages carry no permission of their own: the
      // * whole /settings route is `superAdminOnly` (AuthenticatedAppRoutes.tsx),
      // * and their REST contract declares `superAdminOnly` too. The retired
      // * DASHBOARD_SETTINGS_EDIT added nothing on top and was held by aiTrainer.
      {
        text: 'settings.sidebar.platform-settings.home',
        path: 'home-settings',
        component: HomeSettings,
        permissions: [],
      },
      {
        text: 'settings.sidebar.platform-settings.hot-topics',
        path: 'hot-topics-settings',
        component: HotTopicsSettings,
        permissions: [],
      },
      {
        text: 'settings.sidebar.platform-settings.csat',
        path: 'csat-settings',
        component: CsatSettings,
        permissions: [],
      },
      {
        text: 'settings.sidebar.platform-settings.knowledge-hub',
        path: 'knowledge-hub-settings',
        component: KnowledgeHubSettings,
        permissions: [],
      },
      {
        text: 'settings.sidebar.platform-settings.members',
        path: 'members',
        component: MemberSettings,
        permissions: [PERMISSIONS.COMPANY.COMPANY_MEMBERS_MANAGE],
      },
      {
        text: 'settings.sidebar.platform-settings.roles',
        path: 'roles',
        component: RolesSettings,
        permissions: [PERMISSIONS.ROLE.LIST_ROLES, PERMISSIONS.ROLE.EDIT_ROLES],
      },
    ],
  },
  {
    icon: 'smart_toy',
    title: 'settings.sidebar.bot-settings.title',
    path: 'bot-settings',
    elements: [
      {
        text: 'settings.sidebar.bot-settings.app',
        path: 'app-settings',
        component: AppSettings,
        permissions: [
          PERMISSIONS.APPLICATION.APPLICATION_LIST_SETTINGS,
          PERMISSIONS.APPLICATION.APPLICATION_UPDATE_SETTINGS,
        ],
      },
      {
        text: 'settings.sidebar.bot-settings.export',
        path: 'export-settings',
        component: ExportSettings,
        permissions: [
          PERMISSIONS.APPLICATION.APPLICATION_EXPORT_LIST_SETTINGS,
          PERMISSIONS.APPLICATION.APPLICATION_EXPORT_UPDATE_SETTINGS,
        ],
      },
      {
        text: 'settings.sidebar.bot-settings.debug',
        path: 'debug-settings',
        component: DebugSettings,
        permissions: [
          PERMISSIONS.APPLICATION.APPLICATION_EXPORT_LIST_SETTINGS,
          PERMISSIONS.APPLICATION.APPLICATION_EXPORT_UPDATE_SETTINGS,
        ],
      },
      {
        text: 'settings.sidebar.bot-settings.validation',
        path: 'validation-settings',
        component: ValidationSettings,
        permissions: [
          PERMISSIONS.APPLICATION.APPLICATION_LIST_SETTINGS,
          PERMISSIONS.APPLICATION.APPLICATION_UPDATE_SETTINGS,
        ],
      },
      {
        text: 'settings.sidebar.bot-settings.handover',
        path: 'handover-settings',
        component: HandoverSettings,
        permissions: [
          PERMISSIONS.APPLICATION.APPLICATION_LIST_SETTINGS,
          PERMISSIONS.APPLICATION.APPLICATION_UPDATE_SETTINGS,
        ],
      },
    ],
  },
  {
    icon: 'handyman',
    title: 'settings.sidebar.tools',
    path: 'tools',
    elements: [
      {
        text: 'settings.sidebar.tools.links',
        path: 'links',
        component: LinkSettings,
        permissions: [PERMISSIONS.LINK.MANAGE_LINKS],
      },
      {
        text: 'settings.sidebar.tools.invite-email-template',
        path: 'invite-email-template',
        component: InviteEmailTemplateSettings,
        permissions: [PERMISSIONS.EMAIL_TEMPLATE.INVITE_EMAIL_TEMPLATE_EDIT],
      },
    ],
  },
  {
    icon: 'account_tree',
    title: 'settings.sidebar.flow',
    path: 'flow',
    elements: [
      {
        text: 'settings.sidebar.revisions',
        path: 'revisions',
        component: RevisionsPage,
        permissions: [PERMISSIONS.FLOW.FLOW_REVISION_UPDATE],
      },
      {
        text: 'settings.sidebar.flow.subflow-templates',
        path: 'subflow-templates',
        component: SubflowTemplatesSettings,
        permissions: [PERMISSIONS.FLOW.SUBFLOW_TEMPLATES_LIST],
      },
    ],
  },
];
